import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { LifeBuoy, X, Loader2, Upload } from 'lucide-react';
import { API } from '@/App';
import { toast } from 'sonner';

export default function HelpButton({ user }) {
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    subject: '',
    description: '',
    category: 'general'
  });
  const [screenshot, setScreenshot] = useState(null);

  const handleSubmit = async () => {
    if (!form.subject.trim() || !form.description.trim()) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setSubmitting(true);
    const token = localStorage.getItem('token');
    
    try {
      const ticketData = {
        subject: form.subject,
        description: form.description,
        category: form.category,
        user_email: user?.email || null,
        screenshot_url: null
      };
      
      const response = await fetch(`${API}/help-tickets`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify(ticketData)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success('Help ticket submitted! We\'ll get back to you soon.');
        setShowModal(false);
        setForm({ subject: '', description: '', category: 'general' });
        setScreenshot(null);
      } else {
        // Extract error message from Pydantic validation error
        let errorMessage = 'Failed to submit ticket';
        if (data.detail) {
          if (Array.isArray(data.detail)) {
            errorMessage = data.detail.map(e => e.msg).join(', ');
          } else if (typeof data.detail === 'string') {
            errorMessage = data.detail;
          }
        }
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Ticket submission error:', error);
      toast.error('Failed to submit ticket. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Button
        onClick={() => setShowModal(true)}
        variant="outline"
        size="sm"
        className="fixed bottom-6 right-6 rounded-full w-14 h-14 shadow-lg bg-white hover:bg-violet-50 border-violet-200 z-50"
        title="Get Help"
      >
        <LifeBuoy className="w-6 h-6 text-violet-600" />
      </Button>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <LifeBuoy className="w-6 h-6 text-violet-600" />
                Get Help
              </h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowModal(false)}
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <Label>Subject *</Label>
                <Input
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  placeholder="Brief description of the issue"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label>Category</Label>
                <select
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-3 text-sm"
                >
                  <option value="general">General Question</option>
                  <option value="bug">Bug Report</option>
                  <option value="feature_request">Feature Request</option>
                  <option value="billing">Billing Issue</option>
                </select>
              </div>
              
              <div>
                <Label>Description *</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Please describe the issue in detail. Include steps to reproduce if it's a bug."
                  className="mt-1 min-h-[120px]"
                />
              </div>
              
              <div>
                <Label>Screenshot (Optional)</Label>
                <div className="mt-1">
                  <label className="flex items-center justify-center w-full h-32 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-violet-400 transition-colors">
                    <div className="text-center">
                      <Upload className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                      <p className="text-sm text-slate-600">
                        {screenshot ? screenshot.name : 'Click to upload screenshot'}
                      </p>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => setScreenshot(e.target.files[0])}
                    />
                  </label>
                </div>
              </div>
            </div>
            
            <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setShowModal(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className="bg-violet-600 hover:bg-violet-700 text-white"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Submitting...</>
                ) : (
                  'Submit Ticket'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
